import { AdjustedCalc, CalcHistoryEntry, CommissionLine, PeriodStatus, SnapshotField } from '../types/commission';

export const loanAdjustedCalc: AdjustedCalc = {
  loanAmount: 412500,
  compType: 'Correspondent',
  product: 'not HELOC',
  totalCompPct: 3.1,
  revenue: 12787.5,
  processingCollected: 795,
  underwritingCollected: 1490,
  threshold: 2790,
  feeShort: 252.5,
  revenueAfterShort: 12535,
  adjustedCompPct: 3.0388,
  divisorPct: 3.25,
  divisorRuleKey: 'V1-DIV-CORR',
  adjustedAmount: 385692.31,
  usesRaw: false,
  negative: false,
};

export const loanRecord = {
  loanNumber: 'LN-2026-004812',
  borrower: 'J. Sample',
  loanAmount: 412500,
  adjustedAmount: 385692.31,
  fundedDate: 'Oct 14 2026',
  loanOfficer: 'Alex Rivera',
  branch: 'Troy',
  compType: 'Correspondent',
  product: 'not HELOC',
  leadSource: 'Company',
  period: 'October 2026',
  periodStatus: 'Open' as PeriodStatus,
  nextOpenPeriod: 'October 2026',
};

export const loanDetails: SnapshotField[] = [
  { label: 'Borrower', value: 'J. Sample' },
  { label: 'Loan Amount', value: '$412,500.00' },
  { label: 'Funded Date', value: 'Oct 14 2026' },
  { label: 'Total Comp Corr %', value: '3.10%' },
  { label: 'Processing Collected', value: '$795.00' },
  { label: 'Underwriting Collected', value: '$1,490.00 (Admin fee $0.00, so UW is used)' },
  { label: 'Loan Officer', value: 'Alex Rivera (licensed)' },
  { label: 'Team Lead', value: 'Sam Patel' },
  { label: 'Comp Type', value: 'Correspondent' },
  { label: 'Product', value: 'not HELOC' },
  { label: 'Branch', value: 'Troy' },
  { label: 'Lead Source', value: 'Company' },
];

const loanSnapshot: SnapshotField[] = [
  { label: 'Loan Amount', value: '$412,500.00' },
  { label: 'Adjusted Loan Amount', value: '$385,692.31' },
  { label: 'Total Comp Corr %', value: '3.10%' },
  { label: 'Fee Short (halved)', value: '$252.50' },
  { label: 'Adjusted Total Comp Corr %', value: '3.0388%' },
  { label: 'Divisor', value: '3.25% (V1-DIV-CORR)' },
  { label: 'Comp Type', value: 'Correspondent' },
  { label: 'Product', value: 'not HELOC' },
  { label: 'Funded Date', value: 'Oct 14 2026' },
  { label: 'Branch', value: 'Troy' },
];

export const commissionLines: CommissionLine[] = [
  {
    id: 'cl-base',
    loanNumber: 'LN-2026-004812',
    payee: 'Alex Rivera',
    payeeRole: 'Loan Officer',
    component: 'Base',
    ruleKey: 'V1-BT-TROY-2',
    bps: 125,
    payAmount: 5156.25,
    adjustedAmount: 4821.15,
    period: 'October 2026',
    periodStatus: 'Open',
    status: 'Calculated',
    trace: [
      'Standard LO base BPS comes from the employee’s branch and total loan volume for the period',
      'Alex: Troy, October volume $2,485,400.00 ≥ $1,500,000.00 → 125 bps (V1-BT-TROY-2 in LO Standard Plan v1 parity)',
      'Troy is 100 bps below $1,500,000.00. On main, a blank or other branch (e.g. Remote) gives 0 base BPS (Risk)',
    ],
    formulas: ['Adjusted (pays): $385,692.31 × 1.25% = $4,821.15', 'Stored (reference): $412,500.00 × 1.25% = $5,156.25'],
    snapshot: [...loanSnapshot, { label: 'October 2026 Volume', value: '$2,485,400.00' }, { label: 'Base BPS', value: '125 (Troy ≥ $1.5M)' }],
    adjustedCalc: loanAdjustedCalc,
  },
  {
    id: 'cl-bonus',
    loanNumber: 'LN-2026-004812',
    payee: 'Alex Rivera',
    payeeRole: 'Loan Officer',
    component: 'Monthly Bonus',
    ruleKey: 'V1-MB-3',
    bps: 25,
    payAmount: 1031.25,
    adjustedAmount: 964.23,
    period: 'October 2026',
    periodStatus: 'Open',
    status: 'Calculated',
    trace: [
      'Monthly bonus BPS is driven by the count of closed loans in the month — not funded units, not volume — and applies to Loan Officer fees only',
      'Alex closed 7 loans in October 2026 → +50 bps (V1-MB-3)',
      'Cap: base + bonus can’t exceed 150 bps, so bonus = MIN(50, 150 − 125) = +25 — “+50 capped to +25” (V1-MB-CAP)',
      'The bonus applies to every loan Alex closed in October, including this one. git main CASE pays 0 at 4–6; prod to be confirmed',
    ],
    formulas: ['Adjusted (pays): $385,692.31 × 0.25% = $964.23', 'Stored (reference): $412,500.00 × 0.25% = $1,031.25'],
    snapshot: [...loanSnapshot, { label: 'Closed Loans This Month', value: '7' }, { label: 'Bonus BPS', value: '+50 capped to +25 (150 cap)' }],
    adjustedCalc: loanAdjustedCalc,
  },
  {
    id: 'cl-tl',
    loanNumber: 'LN-2026-004812',
    payee: 'Sam Patel',
    payeeRole: 'Team Lead · Troy',
    component: 'TL Rate',
    ruleKey: 'V1-TL-RATE',
    bps: 10,
    payAmount: 412.5,
    adjustedAmount: 385.69,
    bpsSource: 'From employee record: Team Lead BPS',
    period: 'October 2026',
    periodStatus: 'Open',
    status: 'Calculated',
    trace: [
      'Opportunity Owner (Alex Rivera) ≠ Team Leader (Sam Patel), so the Team Lead row is created — it is skipped only when the Opportunity Owner is the Team Leader (self-gen)',
      'TL BPS 10 – from employee record: Team Lead BPS (Example data). Team Lead rows carry no bonus BPS',
      'Same engine as Loan Officer rows: Adjusted Loan Amount × TL BPS ÷ 10,000',
      'In the Salesforce git repo this flow (NCS Update TL Commission) is marked Obsolete, so this rule is unconfirmed in production',
    ],
    formulas: ['Adjusted (pays): $385,692.31 × 0.10% = $385.69', 'Stored (reference): $412,500.00 × 0.10% = $412.50'],
    snapshot: [
      ...loanSnapshot,
      { label: 'TL BPS', value: '10 – from employee record: Team Lead BPS (Example data)' },
      { label: 'Bonus', value: 'None' },
    ],
    adjustedCalc: loanAdjustedCalc,
  },
];

const splitCalc: AdjustedCalc = {
  loanAmount: 524000,
  compType: 'Correspondent',
  product: 'not HELOC',
  totalCompPct: 3.2,
  revenue: 16768,
  processingCollected: 795,
  underwritingCollected: 1802,
  threshold: 2790,
  feeShort: 96.5,
  revenueAfterShort: 16671.5,
  adjustedCompPct: 3.1816,
  divisorPct: 3.25,
  divisorRuleKey: 'V1-DIV-CORR',
  adjustedAmount: 512969.23,
  usesRaw: false,
  negative: false,
};

export const brokerExample = {
  loanAmount: 300000,
  compPct: 2.5,
  revenue: 7500,
  divisorPct: 2.75,
  divisorRuleKey: 'V1-DIV-BRK',
  adjustedAmount: 272727.27,
};

export const referenceExamples: CommissionLine[] = [
  {
    id: 'ex-split-alex',
    loanNumber: 'LN-2026-004790',
    payee: 'Alex Rivera',
    payeeRole: 'Loan Officer · primary',
    component: 'Base',
    ruleKey: 'V1-BT-TROY-2',
    bps: 125,
    payAmount: 3930,
    adjustedAmount: 3847.27,
    splitPct: 60,
    period: 'October 2026',
    periodStatus: 'Open',
    status: 'Calculated',
    trace: [
      'LN-2026-004790 is split 60 / 40 between Alex Rivera and Jordan Lee (NEW — splits are not in main today)',
      'Alex’s 60% share is paid at Alex’s own rate: Troy, volume ≥ $1.5M → 125 bps (V1-BT-TROY-2). His bonus share is +50 capped to +25: $769.45 adjusted / $786.00 stored',
      'Unit credit stays with the primary LO (Alex)',
    ],
    formulas: ['Adjusted (pays): $512,969.23 × 1.25% × 60% = $3,847.27', 'Stored (reference): $524,000.00 × 1.25% × 60% = $3,930.00'],
    snapshot: [
      { label: 'Loan Amount', value: '$524,000.00' },
      { label: 'Adjusted Loan Amount', value: '$512,969.23' },
      { label: 'Comp Type', value: 'Correspondent' },
      { label: 'Product', value: 'not HELOC' },
      { label: 'Split', value: '60% Alex / 40% Jordan' },
      { label: 'Base BPS', value: '125' },
    ],
    adjustedCalc: splitCalc,
  },
  {
    id: 'ex-split-jordan',
    loanNumber: 'LN-2026-004790',
    payee: 'Jordan Lee',
    payeeRole: 'Loan Officer · split partner',
    component: 'Base',
    ruleKey: 'V1-BT-TROY-2',
    bps: 125,
    payAmount: 2620,
    adjustedAmount: 2564.85,
    splitPct: 40,
    period: 'October 2026',
    periodStatus: 'Open',
    status: 'Calculated',
    trace: [
      'Jordan Lee receives the 40% share of the LN-2026-004790 split (NEW — splits are not in main today)',
      'Jordan: Troy, period volume $1,640,000.00 ≥ $1.5M → base 125 (V1-BT-TROY-2)',
      'Jordan’s bonus share: 5 closed loans → +15 bps: $307.78 adjusted / $314.40 stored',
    ],
    formulas: ['Adjusted (pays): $512,969.23 × 1.25% × 40% = $2,564.85', 'Stored (reference): $524,000.00 × 1.25% × 40% = $2,620.00'],
    snapshot: [
      { label: 'Loan Amount', value: '$524,000.00' },
      { label: 'Adjusted Loan Amount', value: '$512,969.23' },
      { label: 'Comp Type', value: 'Correspondent' },
      { label: 'Product', value: 'not HELOC' },
      { label: 'Split', value: '40% Jordan / 60% Alex' },
      { label: 'Base BPS', value: '125' },
    ],
    adjustedCalc: splitCalc,
  },
  {
    id: 'ex-director',
    loanNumber: 'LN-2026-004835',
    payee: 'Angelo Jarbo',
    payeeRole: 'Miami Director/DL',
    component: 'Director/DL',
    ruleKey: 'V1-DIR-RATE',
    bps: 5,
    payAmount: 180,
    adjustedAmount: 180,
    bpsSource: 'From employee record: Director BPS',
    period: 'October 2026',
    periodStatus: 'Open',
    status: 'Calculated',
    trace: [
      'Morgan Brooks (Miami) is the Opportunity Owner on LN-2026-004835; the Opportunity’s Division Leader is Angelo Jarbo',
      'Opportunity Owner ≠ Division Leader, so the Director row is created and written to the Opportunity’s Division Leader Commissions field. It is skipped when the Opportunity Owner is the Division Leader',
      'Director BPS 5 – from employee record: Director BPS (Example data). Director rows carry no bonus BPS',
    ],
    formulas: ['Adjusted (pays): $360,000.00 × 0.05% = $180.00', 'Stored (reference): $360,000.00 × 0.05% = $180.00'],
    snapshot: [
      { label: 'Loan Amount', value: '$360,000.00' },
      { label: 'Opportunity Owner', value: 'Morgan Brooks' },
      { label: 'Division Leader', value: 'Angelo Jarbo' },
      { label: 'Writes To', value: 'Division Leader Commissions' },
      { label: 'Director BPS', value: '5 – from employee record: Director BPS (Example data)' },
      { label: 'Bonus', value: 'None' },
    ],
  },
  {
    id: 'ex-selfgen',
    loanNumber: 'LN-2026-004866',
    payee: 'Sam Patel',
    payeeRole: 'Team Lead · Troy',
    component: 'TL Rate',
    ruleKey: 'V1-TL-RATE',
    bps: null,
    payAmount: 0,
    adjustedAmount: 0,
    period: 'October 2026',
    periodStatus: 'Open',
    status: 'Skipped',
    trace: [
      'Sam Patel is the Opportunity Owner on LN-2026-004866 and is also the Troy Team Leader',
      'Self-gen — override skipped. The Team Lead row is skipped when the Opportunity Owner = Team Leader',
      'The loan is excluded from Sam’s team volume. Sam is paid his LO base (Troy, own loans < $1.5M → 100 bps, $3,500.00) and bonus (4 closed loans → +15 bps, $525.00) on it instead',
      'Bonus on a Team Lead’s own self-gen loan differs from main (main gives 0 when LO = loan’s Team Leader); Zeyad confirms bonus applies',
    ],
    formulas: ['No Team Lead row: $0.00'],
    snapshot: [
      { label: 'Loan Amount', value: '$350,000.00' },
      { label: 'Opportunity Owner', value: 'Sam Patel' },
      { label: 'Team Leader', value: 'Sam Patel' },
      { label: 'Lead Source', value: 'Self-gen' },
    ],
  },
  {
    id: 'ex-adj',
    loanNumber: 'LN-2026-004398',
    payee: 'Alex Rivera',
    payeeRole: 'Loan Officer',
    component: 'Adjustment',
    ruleKey: 'ADJ-2026-09-0117',
    bps: 150,
    payAmount: 120,
    adjustedAmount: 120,
    period: 'September 2026',
    periodStatus: 'Locked',
    postedTo: 'October 2026',
    status: 'Adjustment',
    trace: [
      'September 2026 is a locked period',
      'Loan amount on LN-2026-004398 was corrected from $310,000.00 to $318,000.00 after the period locked',
      'September rates: base 125 bps (Troy, volume ≥ $1,500,000.00) + bonus +50 capped to +25 (7 closed loans in Sep, 150 cap)',
      'NEW — not in main today: posted as a fixed-dollar Adjustment to October 2026, the next open period. Today a post-funding loan edit silently rewrites the original month’s fee',
    ],
    formulas: ['$8,000.00 × (125 + 25 bps, bonus capped) = $120.00 (same on both bases)'],
    snapshot: [
      { label: 'Original Amount', value: '$310,000.00' },
      { label: 'Corrected Amount', value: '$318,000.00' },
      { label: 'Difference', value: '$8,000.00' },
      { label: 'Sep 2026 BPS', value: '125 + 25 (capped)' },
      { label: 'Source Period', value: 'Sep 2026 (Locked)' },
      { label: 'Posted To', value: 'October 2026' },
    ],
  },
];

export const calcHistory: CalcHistoryEntry[] = [
  {
    id: 'h3',
    timestamp: 'Oct 29 2026',
    title: 'Monthly bonus +15 → +25 bps',
    detail: 'Alex reached 7 closed loans (LN-2026-004903 closed Oct 28) → bonus +50 capped to +25 (150 cap). Bonus lines replaced in place — October is open. Base unchanged at 125 bps.',
  },
  {
    id: 'h2',
    timestamp: 'Oct 16 2026',
    title: 'Underwriting fee $1,490.00 posted',
    detail: 'Fee short $997.50 → $252.50; Adjusted Loan Amount $362,769.23 → $385,692.31.',
  },
  {
    id: 'h1',
    timestamp: 'Oct 15 2026',
    title: 'First calculation',
    detail: 'Base 125 bps (Troy, volume ≥ $1,500,000.00) · 5 closed loans → +15 bps bonus · Team Lead row created at 10 bps from the employee record.',
  },
];
